import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { ppaUrls } from 'config/urls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type WhitespaceKpi = {
    kpi_id?: string;
    value?: string | number | boolean;
    raw_value?: number | null;
    growth_percentage?: number | null;
    trend_direction?: 'up' | 'down' | 'neutral' | string;
    comparison_period?: string;
    valuePrefix?: string;
    valueSuffix?: string;
};

type WhitespaceHierarchyItem = {
    dimension?: string;
    attribute_type?: string;
    ex_id?: string;
    description?: string;
};

export type WhitespaceItem = {
    dimension?: string;
    attribute_type?: string;
    ex_id?: string;
    description?: string;
    is_own_attribute?: boolean;
    hierarchy?: WhitespaceHierarchyItem[];
    kpis?: WhitespaceKpi[];
};

type WhitespaceBucketRecord = {
    kpis?: WhitespaceKpi[];
    data?: WhitespaceItem[];
    total_count?: number;
    limit?: number;
    offset?: number;
};

type WhitespaceBucket = {
    id?: string;
    data?: WhitespaceBucketRecord[];
};

type WhitespaceColumnGroup = {
    ex_id?: string;
    description?: string;
    data?: WhitespaceBucket[];
    total_count?: number;
    limit?: number;
    offset?: number;
};

type WhitespaceGridResponse = {
    data?: WhitespaceColumnGroup[];
    total_count?: number;
    limit?: number;
    offset?: number;
};

export type WhitespaceGridCell = {
    valueSales: string;
    valuePrefix: string;
    valueSuffix: string;
    avgValueSales: string;
    avgPrefix: string;
    sharePercentage: number;
    shareLabel: string;
    crossSectionShareLabel: string;
    items: WhitespaceItem[];
    moreCount: number;
    trendValue: number | null;
    trendDirection: 'up' | 'down' | 'neutral';
    comparisonPeriod: string;
    heatmapColor: string;
    isWhitespace: boolean;
    raw_value: number | null;
};

export type WhitespaceGridRow = {
    bucketLabel: string;
    cells: Record<string, WhitespaceGridCell | null>;
};

type WhitespaceGridData = {
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
};

type AxisPagination = {
    limit?: number;
    offset?: number;
};

type WhitespaceGridPagination = {
    child?: AxisPagination;
    column?: AxisPagination;
    row?: AxisPagination;
};

type WhitespaceGridRequestPayload = Record<string, unknown> & {
    pagination?: WhitespaceGridPagination;
};

type UseWhitespaceAnalysisGridDataParams = {
    gridRequestPayload: Record<string, unknown>;
    yAxis: string;
    xAxis: string;
    crossSection: string;
    startTimestamp?: number;
    endTimestamp?: number;
    idForRestOfComponentLoading: boolean;
};

const EMPTY_GRID_DATA: WhitespaceGridData = {
    columnHeaders: [],
    gridRows: [],
};

const DEFAULT_PAGE_LIMIT = 10;
const normalizeKpiId = (kpiId?: string) => (kpiId ?? '').trim().toLowerCase();

const parseKpiValue = (value: string | number | boolean | undefined) =>
    value === undefined || value === null ? '-' : String(value);

const parseGridCell = (record?: WhitespaceBucketRecord, selectedYAxisKpiId?: string): WhitespaceGridCell | null => {
    if (!record) return null;
    const kpis = record.kpis ?? [];
    const hasItems = (record.data ?? []).length > 0;
    if (kpis.length === 0 && !hasItems) return null;
    const whitespaceKpi = kpis.find((kpi) => kpi.kpi_id === "whitespace_status");
    const valueSalesKpi = kpis.find((kpi) => kpi.kpi_id === "value_sales");
    const heatmapKpi = kpis.find((kpi) => kpi.kpi_id === "heatmap_value");
    const ownShareKpi = kpis.find((kpi) => kpi.kpi_id === "own_share");
    const ownCountKpi = kpis.find((kpi) => kpi.kpi_id === "own_count");
    const avgValueKpi = kpis.find((kpi) => normalizeKpiId(kpi.kpi_id) === normalizeKpiId(selectedYAxisKpiId));

    const items = record.data ?? [];
    const visibleItems = items.slice(0, 4);
    const moreCount = Math.max((record.total_count ?? items.length) - visibleItems.length, 0);

    const ownShareRaw =
        typeof ownShareKpi?.raw_value === 'number' && Number.isFinite(ownShareKpi.raw_value) ? ownShareKpi.raw_value : null;
    const safeShare = ownShareRaw !== null ? Math.max(0, Math.min(ownShareRaw, 100)) : 0;
    const shareLabel = ownShareRaw !== null ? `${ownShareRaw}%` : '-';


    return {
        valueSales: parseKpiValue(valueSalesKpi?.value),
        valuePrefix: valueSalesKpi?.valuePrefix ?? '',
        valueSuffix: valueSalesKpi?.valueSuffix ?? '',
        avgValueSales: parseKpiValue(avgValueKpi?.value),
        avgPrefix: avgValueKpi?.valuePrefix ?? '',
        sharePercentage: safeShare,
        shareLabel,
        crossSectionShareLabel: `${ownCountKpi?.value}/${record.total_count ?? items.length}`,
        items: visibleItems,
        moreCount,
        trendValue: typeof valueSalesKpi?.growth_percentage === 'number' ? valueSalesKpi.growth_percentage : null,
        trendDirection:
            valueSalesKpi?.trend_direction === 'down'
                ? 'down'
                : valueSalesKpi?.trend_direction === 'up'
                  ? 'up'
                  : 'neutral',
        comparisonPeriod: valueSalesKpi?.comparison_period ?? '',
        heatmapColor:
            typeof heatmapKpi?.value === 'string' && heatmapKpi.value.trim() !== ''
                ? heatmapKpi.value
                : '',
        isWhitespace: Boolean(whitespaceKpi?.value),
        raw_value: typeof valueSalesKpi?.raw_value === 'number' ? valueSalesKpi.raw_value : null,
    };
};

const toFiniteNumber = (value: unknown, fallback: number) =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const transformGridData = (gridData?: WhitespaceGridResponse, selectedYAxisKpiId?: string): WhitespaceGridData => {
    const columnGroups = gridData?.data ?? [];
    const columnHeaders = columnGroups.map((group) => group.description ?? group.ex_id ?? '');

    const seen = new Set<string>();
    const bucketLabels: string[] = [];
    columnGroups.forEach((group) => {
        (group.data ?? []).forEach((bucket) => {
            const label = bucket.id ?? '';
            if (!seen.has(label)) {
                seen.add(label);
                bucketLabels.push(label);
            }
        });
    });

    const gridRows: WhitespaceGridRow[] = bucketLabels.map((bucketLabel) => {
        const cells = columnGroups.reduce<Record<string, WhitespaceGridCell | null>>((acc, group) => {
            const columnKey = group.description ?? group.ex_id ?? '';
            const bucket = (group.data ?? []).find((item) => (item.id ?? '') === bucketLabel);
            acc[columnKey] = parseGridCell(bucket?.data?.[0], selectedYAxisKpiId);
            return acc;
        }, {});
        return { bucketLabel, cells };
    });

    return { columnHeaders, gridRows };
};

const mergeGridData = (previousData: WhitespaceGridData, nextData: WhitespaceGridData): WhitespaceGridData => {
    const mergedColumnHeaders = [...previousData.columnHeaders];
    nextData.columnHeaders.forEach((header) => {
        if (!mergedColumnHeaders.includes(header)) {
            mergedColumnHeaders.push(header);
        }
    });

    const rowOrder = previousData.gridRows.map((row) => row.bucketLabel);
    const rowMap = new Map<string, WhitespaceGridRow>(
        previousData.gridRows.map((row) => [row.bucketLabel, { bucketLabel: row.bucketLabel, cells: { ...row.cells } }]),
    );

    nextData.gridRows.forEach((row) => {
        const existingRow = rowMap.get(row.bucketLabel);
        if (!existingRow) {
            rowOrder.push(row.bucketLabel);
            rowMap.set(row.bucketLabel, { bucketLabel: row.bucketLabel, cells: { ...row.cells } });
            return;
        }
        rowMap.set(row.bucketLabel, {
            bucketLabel: row.bucketLabel,
            cells: {
                ...existingRow.cells,
                ...row.cells,
            },
        });
    });

    return {
        columnHeaders: mergedColumnHeaders,
        gridRows: rowOrder.map((bucketLabel) => rowMap.get(bucketLabel) ?? { bucketLabel, cells: {} }),
    };
};

const useWhitespaceAnalysisGridData = ({
    gridRequestPayload,
    yAxis,
    xAxis,
    crossSection,
    startTimestamp,
    endTimestamp,
    idForRestOfComponentLoading,
}: UseWhitespaceAnalysisGridDataParams) => {
    const requestEnabled =
        Boolean(xAxis && yAxis && crossSection) &&
        Number.isFinite(startTimestamp) &&
        Number.isFinite(endTimestamp) &&
        !idForRestOfComponentLoading;

    const requestPayload = gridRequestPayload as WhitespaceGridRequestPayload;
    const selectedYAxisKpiId = Array.isArray(requestPayload.kpi_ids)
        ? String(requestPayload.kpi_ids[0] ?? '')
        : yAxis;
    const pagination = requestPayload.pagination;
    const childPageLimit = toFiniteNumber(pagination?.child?.limit, DEFAULT_PAGE_LIMIT);
    const columnPageLimit = toFiniteNumber(pagination?.column?.limit, DEFAULT_PAGE_LIMIT);
    const rowPageLimit = toFiniteNumber(pagination?.row?.limit, DEFAULT_PAGE_LIMIT);

    const [gridDataState, setGridDataState] = useState<WhitespaceGridData>(EMPTY_GRID_DATA);
    const [totalColumnCount, setTotalColumnCount] = useState(0);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [isFetchingMoreRows, setIsFetchingMoreRows] = useState(false);
    const [isFetchingMoreColumns, setIsFetchingMoreColumns] = useState(false);
    const isFetchingMoreRowsRef = useRef(false);
    const isFetchingMoreColumnsRef = useRef(false);

    const getTotalRowsFromResponse = useCallback((response: WhitespaceGridResponse) => {
        const groupTotals = (response.data ?? [])
            .map((group) => group.total_count)
            .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
        return groupTotals.length > 0 ? Math.max(...groupTotals) : 0;
    }, []);

    const buildGridPayload = useCallback(
        (columnOffset: number, columnLimit: number, rowOffset: number, rowLimit: number): WhitespaceGridRequestPayload => ({
            ...requestPayload,
            pagination: {
                child: {
                    limit: childPageLimit,
                    offset: 0,
                },
                column: {
                    limit: columnLimit,
                    offset: columnOffset,
                },
                row: {
                    limit: rowLimit,
                    offset: rowOffset,
                },
            },
        }),
        [childPageLimit, requestPayload],
    );

    const initialGridPayload = useMemo(
        () => buildGridPayload(0, columnPageLimit, 0, rowPageLimit),
        [buildGridPayload, columnPageLimit, rowPageLimit],
    );

    const {
        data: initialGridResponse,
        isLoading: isGridLoading,
        isFetching: isQueryFetching,
        isError: hasGridError,
        isFetched: isGridFetched,
    } = useQuery<WhitespaceGridResponse, Error>({
        queryKey: ['whitespace-analysis-grid', requestPayload, yAxis, columnPageLimit, rowPageLimit, childPageLimit],
        queryFn: async () => {
            const response = await axios.post(ppaUrls.getWhitespaceAnalysisGridUrl, initialGridPayload);
            return response.data;
        },
        enabled: requestEnabled,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isGridFetching = idForRestOfComponentLoading || isGridLoading || isQueryFetching;
    const shouldTreatGridAsFetched = isGridFetched && !isGridFetching;

    useEffect(() => {
        if (!requestEnabled) {
            setGridDataState(EMPTY_GRID_DATA);
            setTotalColumnCount(0);
            setTotalRowCount(0);
        }
    }, [requestEnabled]);

    useEffect(() => {
        if (!initialGridResponse || !requestEnabled) return;
        const parsedPageData = transformGridData(initialGridResponse, selectedYAxisKpiId);
        setGridDataState(parsedPageData);
        setTotalColumnCount(toFiniteNumber(initialGridResponse.total_count, parsedPageData.columnHeaders.length));
        setTotalRowCount(Math.max(getTotalRowsFromResponse(initialGridResponse), parsedPageData.gridRows.length));
    }, [getTotalRowsFromResponse, initialGridResponse, requestEnabled, selectedYAxisKpiId]);

    const hasMoreRows = totalRowCount > gridDataState.gridRows.length;
    const hasMoreColumns = totalColumnCount > gridDataState.columnHeaders.length;

    const loadMoreRows = useCallback(async () => {
        if (!requestEnabled || !hasMoreRows || isFetchingMoreRowsRef.current || isFetchingMoreColumnsRef.current) return;

        isFetchingMoreRowsRef.current = true;
        setIsFetchingMoreRows(true);

        try {
            const currentRowCount = gridDataState.gridRows.length;
            const currentColumnCount = Math.max(gridDataState.columnHeaders.length, columnPageLimit);
            const payload = buildGridPayload(0, currentColumnCount, currentRowCount, rowPageLimit);
            const response = await axios.post<WhitespaceGridResponse>(ppaUrls.getWhitespaceAnalysisGridUrl, payload);
            const parsedPageData = transformGridData(response.data, selectedYAxisKpiId);
            setGridDataState((previousData) => mergeGridData(previousData, parsedPageData));
            setTotalColumnCount((previous) =>
                Math.max(previous, toFiniteNumber(response.data.total_count, gridDataState.columnHeaders.length)),
            );
            setTotalRowCount((previous) =>
                Math.max(previous, getTotalRowsFromResponse(response.data), gridDataState.gridRows.length + parsedPageData.gridRows.length),
            );
        } finally {
            isFetchingMoreRowsRef.current = false;
            setIsFetchingMoreRows(false);
        }
    }, [
        buildGridPayload,
        columnPageLimit,
        getTotalRowsFromResponse,
        gridDataState.columnHeaders.length,
        gridDataState.gridRows.length,
        hasMoreRows,
        requestEnabled,
        rowPageLimit,
        selectedYAxisKpiId,
    ]);

    const loadMoreColumns = useCallback(async () => {
        if (!requestEnabled || !hasMoreColumns || isFetchingMoreColumnsRef.current || isFetchingMoreRowsRef.current) return;

        isFetchingMoreColumnsRef.current = true;
        setIsFetchingMoreColumns(true);

        try {
            const currentColumnCount = gridDataState.columnHeaders.length;
            const currentRowCount = Math.max(gridDataState.gridRows.length, rowPageLimit);
            const payload = buildGridPayload(currentColumnCount, columnPageLimit, 0, currentRowCount);
            const response = await axios.post<WhitespaceGridResponse>(ppaUrls.getWhitespaceAnalysisGridUrl, payload);
            const parsedPageData = transformGridData(response.data, selectedYAxisKpiId);
            setGridDataState((previousData) => mergeGridData(previousData, parsedPageData));
            setTotalColumnCount((previous) =>
                Math.max(previous, toFiniteNumber(response.data.total_count, gridDataState.columnHeaders.length + parsedPageData.columnHeaders.length)),
            );
            setTotalRowCount((previous) =>
                Math.max(previous, getTotalRowsFromResponse(response.data), gridDataState.gridRows.length),
            );
        } finally {
            isFetchingMoreColumnsRef.current = false;
            setIsFetchingMoreColumns(false);
        }
    }, [
        buildGridPayload,
        columnPageLimit,
        getTotalRowsFromResponse,
        gridDataState.columnHeaders.length,
        gridDataState.gridRows.length,
        hasMoreColumns,
        requestEnabled,
        rowPageLimit,
        selectedYAxisKpiId,
    ]);

    return {
        columnHeaders: gridDataState.columnHeaders,
        gridRows: gridDataState.gridRows,
        isGridLoading,
        isGridFetching,
        hasGridError,
        isGridFetched: shouldTreatGridAsFetched,
        loadMoreRows,
        loadMoreColumns,
        hasMoreRows,
        hasMoreColumns,
        isFetchingMoreRows,
        isFetchingMoreColumns,
    };
};

export default useWhitespaceAnalysisGridData;
